import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { IInvestmentYear, IPropertyEntity, IPropertyYear, PropertyService } from 'src/app/services/poperty.service';
import { Location } from '@angular/common';
import { BehaviorSubject, filter, map, merge, Observable, of, switchMap } from 'rxjs';
import { IPageLoaderViewModel } from 'src/app/components/page-loader/page-loader.component';
import { IPageHeaderViewModel } from 'src/app/components/page-header/page-header.component';
import { DateUtil } from 'src/app/lib/date-util';
import { FormOkCancelComponent, IFormOkCancelViewModel } from 'src/app/forms/form-ok-cancel/form-ok-cancel.component';
import { IFormViewModel } from 'src/app/forms/forms';

@Component({
  selector: 'app-property-year-page',
  templateUrl: './property-year-page.component.html',
  styleUrls: ['./property-year-page.component.scss']
})
export class PropertyYearPageComponent implements OnInit {

  pageVm$: Observable<IPageViewModel>;

  /**
   * 
   * @param propertyService 
   * @param activatedRoute 
   * @param location 
   * @param router 
   */
  constructor(private propertyService: PropertyService, private activatedRoute: ActivatedRoute,
    private location: Location, private router: Router) { }

  private refresher$ = new BehaviorSubject<{ propertyCode: string, calendarYear: string }>(undefined);
  
  //#region generic form stuff

  /**
   * 
   * @param pvm 
   * 
   */
  formCancel(pvm: IPageViewModel) {
    pvm.contentVm.formVm = undefined;
    pvm.pageHeaderVm.formDisplayed = false;
  }

  //#endregion generic form stuff

  /**
   * 
   * @param vm 
   */
  backClicked(vm: IContentViewModel) {
    this.location.back();
  }

  /**
   * 
   * @param vm 
   */
  editClicked(pvm: IPageViewModel) {
    this.editFormStart(pvm.contentVm);
    pvm.pageHeaderVm.formDisplayed = true;
  }

  /**
   * 
   * @param vm 
   * @returns 
   */
  prevClicked(vm: IContentViewModel) {
    if (vm.investmentYear.calendarYear - 1 < vm.property.investmentYears[0].calendarYear)
      return;

    const prevYear = vm.investmentYear.calendarYear - 1;
    this.router.navigate(['properties', vm.property.code, 'years', prevYear], { replaceUrl: true });
  }

  /**
   * 
   * @param vm 
   * @returns 
   */
  nextClicked(vm: IContentViewModel) {
    if (vm.investmentYear.calendarYear + 1 > vm.property.investmentYears[vm.property.investmentYears.length - 1].calendarYear)
      return;

    const nextYear = vm.investmentYear.calendarYear + 1;
    this.router.navigate(['properties', vm.property.code, 'years', nextYear], { replaceUrl: true });
  }

  /**
   * 
   */
  ngOnInit(): void {
    const watcher$ = merge(
      this.activatedRoute.paramMap.pipe(
        map((pm: ParamMap) => ({
          propertyCode: pm.get('property-code'),
          calendarYear: pm.get('year'),
        }))
      ),
      this.refresher$
    ).pipe(
      filter(ctx => ctx != undefined),
      switchMap(ctx => {

        const pf$ = this.propertyService.propertyFind(ctx.propertyCode).pipe(
          map(property => ({ ...ctx, property: property }))
        ).pipe(
          map(ctx => {
            const investmentYear = ctx.property.investmentYears.find(py => py.calendarYear.toString() == ctx.calendarYear);
            const currentYear = DateUtil.now().getFullYear() == investmentYear.calendarYear;

            const ret: IPageViewModel = {
              pageHeaderVm: {
                title: ctx.property.propertyInfo.name,
                subTitle: `${investmentYear.calendarYear} - Year ${investmentYear.year}${currentYear ? ' (current)' : ''}`,
                edit: true,
                navVm: {
                  prevEnabled: ctx.property.investmentYears[0].calendarYear < investmentYear?.calendarYear ? true : false,
                  nextEnabled: ctx.property.investmentYears[ctx.property.investmentYears.length - 1].calendarYear > investmentYear?.calendarYear ? true : false
                }
              },
              contentVm: {
                property: ctx.property,
                investmentYear: investmentYear,
              }
            };
            return ret;
          })
        );

        return merge(pf$, of({ workingText: 'Loading...' }));
      })

    );

    this.pageVm$ = watcher$;
  }

  //#region edit form

  /**
   * 
   * @param vm 
   */
  private editFormStart(vm: IContentViewModel) {
    vm.formVm = this.editFormVm(vm);
  }

  /**
   * 
   * @param pvm 
   * @param fvm 
   */
  async editFormComplete(pvm: IPageViewModel, fvm: IEditYearFormViewModel) {

    const ret = await FormOkCancelComponent.processOkAsync(fvm.okCancelVm, 'Saving...', () => this.propertyService.propertySave(fvm.property));
    if (ret) {
      this.formCancel(pvm);
      this.refresher$.next({ propertyCode: pvm.contentVm.property.code, calendarYear: pvm.contentVm.investmentYear.calendarYear.toString() });
    }
  }

  /**
   * 
   * @param vm 
   * @returns 
   */
  private editFormVm(vm: IContentViewModel): IFormViewModel {
    const property = JSON.parse(JSON.stringify(vm.property));
    const investmentYear = JSON.parse(JSON.stringify(vm.investmentYear));

    const ret: IEditYearFormViewModel = {
      type: 'edit',
      title: 'Edit Year',
      property: property,
      investmentYear: investmentYear,

      // todo... edit stuff here

      okCancelVm: FormOkCancelComponent.okCancelVm()
    };

    this.editFormChanged(vm, ret);

    return ret;
  }

  /**
   * 
   * @param vm 
   * @param fvm
   * 
   */
  editFormChanged(vm: IContentViewModel, fvm: IFormViewModel) {
    const issues = [
      false
    ].filter(v => !v);
    fvm.okCancelVm.isValid = issues.length == 0;
  }

  //#endregion edit form

}

/**
 * 
 */
interface IPageViewModel extends IPageLoaderViewModel {
  contentVm?: IContentViewModel;
  pageHeaderVm?: IPageHeaderViewModel;
}

/**
 * 
 */
interface IContentViewModel {
  property: IPropertyEntity;
  investmentYear: IInvestmentYear;

  formVm?: any;
}

/**
 * 
 */
interface IEditYearFormViewModel extends IFormViewModel {
  property: IPropertyEntity;
  investmentYear: IInvestmentYear;
}